import { useState, type MouseEvent, type ReactNode } from 'react';
import { UI_REGISTRY, type UIIconName } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

/**
 * True for clicks the browser should handle natively (open-in-new-tab /
 * new-window): any non-primary button, or primary + a modifier key. Plain
 * left-clicks return false so the caller can intercept them for SPA nav.
 */
function isModifiedClick(e: MouseEvent): boolean {
  return e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
}

export interface SideNavItem {
  readonly label: string;
  readonly href?: string;
  readonly icon?: UIIconName;
  readonly active?: boolean;
  readonly badge?: ReactNode;
  readonly onClick?: () => void;
}

export interface SideNavSection {
  readonly title?: string;
  readonly items: readonly SideNavItem[];
  /**
   * Render this section as a COLLAPSIBLE group: a clickable header (icon +
   * title + optional aggregate `badge` + chevron) that shows/hides its items.
   * Lets a long nav collapse to a short list of category headers. When false /
   * omitted the section renders flat (the original behaviour).
   */
  readonly collapsible?: boolean;
  /** Initial open state for a collapsible section (default closed). */
  readonly defaultOpen?: boolean;
  /** Icon shown on a collapsible group header. */
  readonly icon?: UIIconName;
  /** Aggregate badge shown on the group header (e.g. summed pending counts). */
  readonly badge?: ReactNode;
}

export interface SideNavProps {
  readonly sections: readonly SideNavSection[];
  readonly footer?: ReactNode;
  readonly className?: string;
  /**
   * Optional SPA-navigation hook. When provided, items rendered as anchors
   * (those with `href`) intercept plain left-clicks — calling `onNavigate(href)`
   * instead of triggering a full-page reload — while still letting
   * cmd/ctrl/shift/middle-clicks open the link in a new tab. This gives real
   * anchor semantics (right-click → open in new tab, screen-reader "link"
   * role, `aria-current` on the active page) WITHOUT the full-reload cost.
   * When omitted, anchors fall back to the browser's default navigation.
   *
   * ui-web stays router-agnostic: the consuming app passes its router's
   * navigate (e.g. react-router's `useNavigate()`), not a `<Link>`.
   */
  readonly onNavigate?: (href: string) => void;
  readonly ariaLabel?: string;
}

export function SideNav({ sections, footer, className, onNavigate, ariaLabel }: SideNavProps) {
  const { t } = useI18n();
  // Per-group open overrides keyed by section title. Empty initially so each
  // group falls back to its `defaultOpen` (which the consumer sets true for the
  // active group) — and once the user toggles a group, their choice sticks.
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});

  const renderItem = (item: SideNavItem): ReactNode => {
    const Icon = item.icon ? UI_REGISTRY[item.icon] : null;
    const inner = (
      <>
        {Icon ? <Icon size={18} color="currentColor" /> : null}
        <span className="flex-1 truncate">{item.label}</span>
        {item.badge}
      </>
    );
    const cls = cn(
      'relative inline-flex items-center gap-3 ps-3 pe-2 min-h-10 [@media(pointer:coarse)]:min-h-11 rounded-md text-small font-medium',
      'transition-[color,background-color] duration-(--duration-tap) motion-reduce:transition-none',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text',
      item.active
        ? 'bg-accent-soft text-accent-text font-semibold before:absolute before:inset-y-3 before:start-0 before:w-0.5 before:rounded-full before:bg-accent-text'
        : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
    );
    const href = item.href;
    const ariaCurrent = item.active ? ('page' as const) : undefined;
    return href !== undefined ? (
      <a
        key={item.label}
        href={href}
        aria-current={ariaCurrent}
        className={cls}
        onClick={(e) => {
          if (!onNavigate || isModifiedClick(e)) return;
          e.preventDefault();
          onNavigate(href);
        }}
      >
        {inner}
      </a>
    ) : (
      <button
        key={item.label}
        type="button"
        onClick={item.onClick}
        aria-current={ariaCurrent}
        className={cls}
      >
        {inner}
      </button>
    );
  };

  const titleCls =
    'px-2 pt-3 pb-1 text-caption font-semibold uppercase tracking-wider text-ink-2 font-mono';

  return (
    <nav
      aria-label={
        ariaLabel ?? t('common:navigation.sections', { defaultValue: 'Section navigation' })
      }
      className={cn('w-full p-3 flex flex-col gap-3', className)}
    >
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1.5">
        {sections.map((section, i) => {
          const key = section.title ?? `__section_${i}`;

          if (!section.collapsible) {
            return (
              <div key={key} className="flex flex-col gap-1">
                {section.title ? <div className={titleCls}>{section.title}</div> : null}
                {section.items.map(renderItem)}
              </div>
            );
          }

          const open = openOverrides[key] ?? section.defaultOpen ?? false;
          const Icon = section.icon ? UI_REGISTRY[section.icon] : null;
          return (
            <div key={key} className="flex flex-col gap-1">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpenOverrides((m) => ({ ...m, [key]: !open }))}
                className="w-full inline-flex items-center gap-2 ps-2 pe-2 min-h-10 [@media(pointer:coarse)]:min-h-11 rounded-md text-caption font-semibold uppercase tracking-wider font-mono text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors duration-(--duration-tap) motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
              >
                {Icon ? <Icon size={14} color="currentColor" /> : null}
                <span className="flex-1 text-start truncate">{section.title}</span>
                {!open && section.badge ? section.badge : null}
                <Chevron open={open} />
              </button>
              {open ? (
                <div className="flex flex-col gap-1 ms-2 ps-2 border-s border-hairline">
                  {section.items.map(renderItem)}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {footer ? <div className="border-t border-hairline pt-3">{footer}</div> : null}
    </nav>
  );
}

function Chevron({ open }: { readonly open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn(
        'shrink-0 transition-transform duration-(--duration-tap) motion-reduce:transition-none rtl:rotate-180',
        open ? 'rotate-90 rtl:rotate-90' : '',
      )}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
